// ── storeLocale.js — 매장 국가·통화 (V108) ────────────────────────────────
//
// 매장이 어느 나라에 있는지 하나로 세 가지가 갈린다:
//   1) 영수증 OCR 경로  — 한국은 CLOVA, 중국은 Claude 비전 (CLOVA가 간체 미지원)
//   2) 금액 표시 단위    — 원 / ¥
//   3) 세무 기능 의미     — 한국 부가세·종소세는 중국 매장에 해당 없음
//
// 표시 언어(lang)와는 다른 축이다. 언어는 "보는 사람"의 설정이고,
// 국가는 "매장"의 속성이다. 허페이 매장을 한국어로 보는 사장님도 금액은 ¥로 봐야 한다.
// 그래서 lang은 users/{본인uid}, country는 users/{매장owner uid}에 저장한다.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './db';

export const COUNTRIES = [
  { id: 'KR', label: '한국', native: '한국', currency: 'KRW', symbol: '원', locale: 'ko-KR' },
  { id: 'CN', label: '중국', native: '中国', currency: 'CNY', symbol: '¥',  locale: 'zh-CN' },
];

export const DEFAULT_COUNTRY = 'KR';

export function countryMeta(country) {
  return COUNTRIES.find(c => c.id === country) || COUNTRIES[0];
}

/**
 * 금액 표시. 한국은 "12,000원", 중국은 "¥12,000" / "¥12.50".
 * 위안은 소수점 둘째 자리(分)까지 쓰는 게 보통이라 반올림하지 않는다.
 * V123: 최소 단위보다 작은 금액(1원 미만 / ¥0.01 미만)이 0으로 보이지 않게 유효숫자 두 자리로.
 *       레시피 1인분 원가·식재료 한 줄 원가처럼 아주 작은 값이 "0원"·"¥0.00"으로 보이던 문제.
 */
const tinyNumber = (v) => {
  const a = Math.abs(v);
  const digits = Math.min(6, Math.max(1, 1 - Math.floor(Math.log10(a))));   // 유효숫자 두 자리: 0.23 / 0.043 / 0.0043
  return String(Number(v.toFixed(digits)));
};

export function fmtMoney(n, country = DEFAULT_COUNTRY) {
  const v = Number(n) || 0;
  if (country === 'CN') {
    if (v !== 0 && Math.abs(v) < 0.0001) return v < 0 ? '-<¥0.0001' : '<¥0.0001';   // V124: 그보다 작으면 의미 없는 자릿수
    if (v !== 0 && Math.abs(v) < 0.01) return `¥${tinyNumber(v)}`;
    const s = Math.abs(v % 1) > 0.0001
      ? v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : Math.round(v).toLocaleString('zh-CN');
    return `¥${s}`;
  }
  if (v !== 0 && Math.abs(v) < 0.01) return v < 0 ? '-<0.01원' : '<0.01원';   // V124
  if (v !== 0 && Math.abs(v) < 1) return `${tinyNumber(v)}원`;
  return `${Math.round(v).toLocaleString('ko-KR')}원`;
}

/** 숫자 없이 단위만 (표 헤더 "단가 (원)" 같은 곳) */
export function moneyUnit(country = DEFAULT_COUNTRY) {
  return country === 'CN' ? '¥' : '원';
}

/** "9,500원/kg" 처럼 뒤에 붙는 형태 */
export function fmtMoneyPer(n, per, country = DEFAULT_COUNTRY) {
  return `${fmtMoney(n, country)}/${per}`;
}

/**
 * 매장(owner)의 국가 설정을 구독한다.
 * 매니저 모드에서는 effectiveOwnerUid를 넘겨야 그 매장 기준으로 보인다.
 */
export function useStoreCountry(ownerUid) {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ownerUid) { setLoading(false); return; }
    const unsub = onSnapshot(
      doc(db, 'users', ownerUid),
      (snap) => {
        const c = snap.exists() ? snap.data()?.country : null;
        setCountry(c === 'CN' || c === 'KR' ? c : DEFAULT_COUNTRY);
        setLoading(false);
      },
      (e) => {
        // 오프라인이면 기본값(한국)으로 계속 간다
        console.debug('[storeLocale] 국가 설정 구독 실패:', e?.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [ownerUid]);

  return { country, loading, meta: countryMeta(country) };
}
